//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

const CLIENT_ID_KEY = 'ronto-client-id';

/* localStorage 不可用(隱私模式/停用)時,退回本次載入期間穩定的記憶體值,避免整站 API 掛掉 */
let inMemoryClientId: string | null = null;

const generateUuid = (): string => {
  /* 首選:crypto.randomUUID 僅存在於 secure context(HTTPS / localhost) */
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  /* 次選:crypto.getRandomValues 在非 secure context(區網 HTTP)也可用,手動組 UUID v4 */
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  /* 最後手段:非密碼學等級,僅為避免拋錯(clientId 僅用於裝置識別,碰撞後果不涉安全) */
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const getClientId = (): string => {
  /* 已解析過就直接回,省掉 interceptor 每支 API 重讀 localStorage */
  if (inMemoryClientId) return inMemoryClientId;

  let id: string | null = null;
  try {
    id = localStorage.getItem(CLIENT_ID_KEY);
  } catch {
    /* 讀取失敗(隱私模式等),往下產生新的 */
  }

  if (!id) {
    id = generateUuid();
    try {
      localStorage.setItem(CLIENT_ID_KEY, id);
    } catch {
      /* 寫入失敗:靠 inMemoryClientId 維持本次 session 一致 */
    }
  }

  inMemoryClientId = id;
  return id;
};
